import { useEffect, useRef, useState } from 'react'
import { useRepos } from '../../state/repos'
import { useAuth } from '../../state/auth'
import {
  createClient,
  pingApi,
  AVAILABLE_MODELS,
  DEFAULT_MODEL,
} from '../../anthropic/client'
import { exportBackup, importBackup, summaryLine } from '../../db/backup'

export function SettingsTab() {
  const { settings } = useRepos()
  const { lock, changePassword } = useAuth()

  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState(DEFAULT_MODEL)
  const [status, setStatus] = useState<{ kind: 'ok' | 'err' | 'info'; text: string } | null>(null)
  const [testing, setTesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const [backupBusy, setBackupBusy] = useState(false)
  const [backupMsg, setBackupMsg] = useState<{ kind: 'ok' | 'err' | 'info'; text: string } | null>(null)
  const importInputRef = useRef<HTMLInputElement | null>(null)

  const [pwCurrent, setPwCurrent] = useState('')
  const [pwNew, setPwNew] = useState('')
  const [pwConfirm, setPwConfirm] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const [pwMsg, setPwMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    void (async () => {
      const current = await settings.get()
      if (current) {
        setApiKey(current.anthropicApiKey ?? '')
        setModel(current.preferredModel ?? DEFAULT_MODEL)
      }
      setLoaded(true)
    })()
  }, [settings])

  async function save() {
    setStatus(null)
    setSaving(true)
    try {
      await settings.set({
        anthropicApiKey: apiKey || undefined,
        preferredModel: model,
      })
      setStatus({ kind: 'ok', text: 'Impostazioni salvate.' })
    } catch (e) {
      setStatus({
        kind: 'err',
        text: `Errore salvataggio: ${e instanceof Error ? e.message : 'sconosciuto'}`,
      })
    } finally {
      setSaving(false)
    }
  }

  async function testConnection() {
    setTesting(true)
    setStatus(null)
    try {
      const client = createClient({ apiKey })
      const reply = await pingApi(client, model)
      setStatus({
        kind: 'ok',
        text: `Connessione OK. Risposta: "${reply.slice(0, 40)}"`,
      })
    } catch (e) {
      setStatus({
        kind: 'err',
        text: `Errore: ${e instanceof Error ? e.message : 'sconosciuto'}`,
      })
    } finally {
      setTesting(false)
    }
  }

  async function downloadBackup() {
    setBackupBusy(true)
    setBackupMsg(null)
    try {
      const { json, summary } = await exportBackup()
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const stamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-')
      a.href = url
      a.download = `rd-studio-backup_${stamp}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      setBackupMsg({
        kind: 'ok',
        text: `Backup creato: ${summaryLine(summary)}`,
      })
    } catch (e) {
      setBackupMsg({
        kind: 'err',
        text: `Errore export: ${e instanceof Error ? e.message : 'sconosciuto'}`,
      })
    } finally {
      setBackupBusy(false)
    }
  }

  function triggerImport() {
    importInputRef.current?.click()
  }

  async function handleImportFile(file: File) {
    const ok = confirm(
      'Importare il backup sostituirà TUTTI i dati locali (libro, capi, progetti, impostazioni). ' +
        'Continuare?\n\nDopo l\'importazione l\'app si riavvierà e dovrai sbloccare con la password ' +
        'usata al momento del backup.',
    )
    if (!ok) return

    setBackupBusy(true)
    setBackupMsg(null)
    try {
      const text = await file.text()
      const summary = await importBackup(text)
      setBackupMsg({
        kind: 'ok',
        text: `Backup importato: ${summaryLine(summary)}. Riavvio in corso…`,
      })
      lock()
      // Ricarico per ripartire da uno stato pulito (auth reinizializzato dal vault del backup)
      setTimeout(() => window.location.reload(), 800)
    } catch (e) {
      setBackupMsg({
        kind: 'err',
        text: `Errore import: ${e instanceof Error ? e.message : 'sconosciuto'}`,
      })
    } finally {
      setBackupBusy(false)
    }
  }

  async function submitPasswordChange() {
    setPwMsg(null)
    if (!pwCurrent) {
      setPwMsg({ kind: 'err', text: 'Inserisci la password corrente' })
      return
    }
    if (pwNew.length < 8) {
      setPwMsg({ kind: 'err', text: 'La nuova password deve avere almeno 8 caratteri' })
      return
    }
    if (pwNew !== pwConfirm) {
      setPwMsg({ kind: 'err', text: 'Le due nuove password non coincidono' })
      return
    }
    if (pwNew === pwCurrent) {
      setPwMsg({ kind: 'err', text: 'La nuova password coincide con quella attuale' })
      return
    }
    setPwBusy(true)
    try {
      await changePassword(pwCurrent, pwNew)
      setPwMsg({ kind: 'ok', text: 'Password aggiornata. Conserva la nuova password in un posto sicuro.' })
      setPwCurrent('')
      setPwNew('')
      setPwConfirm('')
    } catch (e) {
      setPwMsg({
        kind: 'err',
        text: e instanceof Error ? e.message : 'Errore cambio password',
      })
    } finally {
      setPwBusy(false)
    }
  }

  if (!loaded) return <p className="muted">Caricamento…</p>

  return (
    <section className="tab-section">
      <h3>Impostazioni</h3>

      <div className="detail-block">
        <h4>API Anthropic</h4>
        <label>
          Chiave API
          <input
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="sk-ant-..."
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <p className="hint">
          La chiave viene cifrata con la master password e salvata localmente.
          Viene usata solo per chiamate dirette a api.anthropic.com. Per limitare
          il rischio, imposta dal dashboard Anthropic un <em>spend limit</em>{' '}
          mensile sulla key.
        </p>

        <label>
          Modello preferito
          <select value={model} onChange={e => setModel(e.target.value)}>
            {AVAILABLE_MODELS.map(m => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        <div className="row">
          <button onClick={save} disabled={saving}>
            {saving ? 'Salvataggio…' : 'Salva'}
          </button>
          <button className="secondary" onClick={testConnection} disabled={testing || !apiKey}>
            {testing ? 'Test in corso…' : 'Verifica connessione'}
          </button>
        </div>

        {status && <div className={`status status-${status.kind}`}>{status.text}</div>}
      </div>

      <div className="detail-block">
        <h4>Backup &amp; ripristino</h4>
        <p className="hint" style={{ marginTop: 0 }}>
          Il backup contiene <strong>tutti</strong> i dati locali (libro, capi,
          progetti, versioni, impostazioni) e il vault con la master password.
          I contenuti sono <strong>già cifrati</strong>: per ripristinare ti
          servirà la password usata al momento del backup. Conservane sempre
          una copia su un disco esterno o cloud personale.
        </p>
        <div className="row">
          <button onClick={downloadBackup} disabled={backupBusy}>
            {backupBusy ? 'Lavoro in corso…' : 'Esporta backup'}
          </button>
          <button className="secondary" onClick={triggerImport} disabled={backupBusy}>
            Importa backup
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={async e => {
              const f = e.target.files?.[0]
              if (f) await handleImportFile(f)
              e.target.value = ''
            }}
          />
        </div>
        {backupMsg && <div className={`status status-${backupMsg.kind}`}>{backupMsg.text}</div>}
      </div>

      <div className="detail-block">
        <h4>Cambia master password</h4>
        <p className="hint" style={{ marginTop: 0 }}>
          Cambia la password senza ri-cifrare i dati: la chiave dei contenuti
          (DEK) viene riavvolta con la nuova password. Tutti i record cifrati
          restano validi.
        </p>
        <label>
          Password attuale
          <input
            type="password"
            value={pwCurrent}
            onChange={e => setPwCurrent(e.target.value)}
            autoComplete="current-password"
            spellCheck={false}
          />
        </label>
        <label>
          Nuova password
          <input
            type="password"
            value={pwNew}
            onChange={e => setPwNew(e.target.value)}
            autoComplete="new-password"
            spellCheck={false}
          />
        </label>
        <label>
          Conferma nuova password
          <input
            type="password"
            value={pwConfirm}
            onChange={e => setPwConfirm(e.target.value)}
            autoComplete="new-password"
            spellCheck={false}
          />
        </label>
        <div className="row">
          <button onClick={submitPasswordChange} disabled={pwBusy}>
            {pwBusy ? 'Cambio in corso…' : 'Cambia password'}
          </button>
        </div>
        {pwMsg && <div className={`status status-${pwMsg.kind}`}>{pwMsg.text}</div>}
      </div>
    </section>
  )
}
